import React, { useState, useCallback } from 'react';
import classnames from 'classnames';
import ScrollableContent from '@wix/da-ds/pkg/ScrollableContent';
import { useStream } from '../utils/hooks/useStream';

export interface Props {
  id?: string;
  role?: string;
  streamId: string;

  className?: string;
  wrapperClassName?: string;
  bottomedClassName?: string;
  scrolledClassName?: string;
  throttleTimeout?: number;
  loadMoreDistance?: number;

  onScrolled?: (scrollTop: number, distanceToBottom: number) => void;
  innerRef?: (node: HTMLElement | null) => void;

  children: React.ReactNode;
}

export const StreamedList: React.FC<Props> = ({
  id,
  role,
  streamId,
  className,
  wrapperClassName,
  bottomedClassName,
  scrolledClassName,
  throttleTimeout = 300,
  loadMoreDistance = 80,
  onScrolled,
  innerRef,
  children,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { hasMore, isFetching, errorMessage, isInitialized, fetchNext } =
    useStream(streamId, throttleTimeout);

  const isEmpty = !isInitialized || (!children && !isFetching);
  const hasError = !!errorMessage;

  const onListScroll = useCallback(
    (scrollTop: number, distanceToBottom: number) => {
      // use isEmpty as a 'hasBeenInitialized' and don't try to
      // fetch more (which can happen if the container is small
      // enough to trigger the distance check below)
      if (
        !isEmpty &&
        distanceToBottom < loadMoreDistance &&
        !isFetching &&
        !hasError &&
        hasMore
      ) {
        fetchNext();
      }

      const isScrolledNew = scrollTop !== 0;
      const isScrolledBottomNew = distanceToBottom < 1;
      if (
        isScrolled !== isScrolledNew ||
        isScrolledToBottom !== isScrolledBottomNew
      ) {
        // let's only update state when something actually changed because things can get laggy
        setIsScrolled(isScrolledNew);
        setIsScrolledToBottom(isScrolledBottomNew);
      }

      onScrolled && onScrolled(scrollTop, distanceToBottom);
    },
    [
      isEmpty,
      loadMoreDistance,
      isFetching,
      hasError,
      hasMore,
      fetchNext,
      isScrolled,
      isScrolledToBottom,
      onScrolled,
    ]
  );

  return (
    <ScrollableContent
      id={id}
      role={role}
      className={className}
      throttleTimeout={throttleTimeout}
      onScrolled={onListScroll}
      wrapperClassName={classnames(
        wrapperClassName,
        isScrolled && scrolledClassName,
        isScrolledToBottom && bottomedClassName
      )}
      innerRef={innerRef}
    >
      {children}
    </ScrollableContent>
  );
};
StreamedList.displayName = 'StreamedList';

export default StreamedList;
